import { loadPdfJs, loadPdfLib } from "./pdf_libs"
// Shared with the server sidecar (priv/pdf/compose.mjs) so both fill paths
// draw fields identically. It lives in priv/ because that is what ships in
// releases; esbuild bundles it from here.
import { drawFields } from "../../../priv/pdf/draw_fields.mjs"
import { openSignatureModal, removeSignatureModal } from "./signature_modal"

/**
 * PdfFormFiller LiveView hook
 *
 * Renders a PDF with HTML input overlays at custom field positions.
 * Signature/initials fields get a draw+type capture pad.
 * On submit, uses pdf-lib to draw values and embed signature images
 * onto the PDF, then uploads the result via LiveView upload.
 *
 * Data attributes:
 *   data-url       - URL to fetch the PDF from
 *   data-scale     - Render scale (default: 1.0)
 *   data-fields    - JSON string of custom field definitions
 *   data-signer-name   - prefills the typed signature (signature fields only)
 *   data-consent-text  - e-sign consent line shown in the signature modal
 *
 * Pushes events:
 *   pdf_loaded     - {page_count}
 *   pdf_error      - {message}
 *   custom_submit  - {field_values, signatures, esign_consent} on submit
 *
 * Handles events:
 *   go_to_page     - {page}
 */
const PdfFormFiller = {
  mounted() {
    this.currentPage = 1
    this.pageCount = 0
    this.scale = parseFloat(this.el.dataset.scale) || 1.0
    this.pdfDoc = null
    this.rendering = false
    this.pendingPage = null
    this.fields = []
    this._pageHeights = {}
    this.multiSigner = this.el.dataset.multiSigner === "true"
    this.signerName = this.el.dataset.signerName || ""
    this.consentText = this.el.dataset.consentText || ""

    try {
      this.fields = JSON.parse(this.el.dataset.fields || "[]")
    } catch (_) {}

    this._wrapper = this.el.querySelector("[data-pdf-wrapper]") || this.el
    this._showLoading()

    const url = this.el.dataset.url
    if (url) this._loadPdf(url)

    this.handleEvent("go_to_page", ({ page }) => this._goToPage(page))

    this._setupPageNav()

    // The container is a div, not a form, so a click on the button is the
    // only submit signal we get.
    this.el.addEventListener("click", (e) => {
      if (e.target.closest("[data-submit-btn]")) {
        e.preventDefault()
        this._handleSubmit()
      }
    })
  },

  destroyed() {
    if (this.pdfDoc) {
      this.pdfDoc.destroy()
      this.pdfDoc = null
    }
    removeSignatureModal()
  },

  // =========================================================================
  // PDF Loading & Rendering
  // =========================================================================

  _showLoading() {
    this._wrapper.innerHTML = `
      <div class="flex items-center justify-center min-h-48 text-base-content/40">
        <span class="loading loading-spinner loading-md"></span>
      </div>
    `
  },

  async _loadPdf(url) {
    this._loadedUrl = url
    this._pdfUrl = url
    this._showLoading()

    try {
      const response = await fetch(url)
      this._originalPdfBytes = await response.arrayBuffer()
      const pdfjsLib = await loadPdfJs()
      this.pdfDoc = await pdfjsLib.getDocument({ data: this._originalPdfBytes.slice(0) }).promise
      this.pageCount = this.pdfDoc.numPages

      for (let i = 1; i <= this.pageCount; i++) {
        const page = await this.pdfDoc.getPage(i)
        const vp = page.getViewport({ scale: 1.0 })
        this._pageHeights[i] = vp.height
      }

      this.currentPage = 1
      this._wrapper.innerHTML = ""
      this._buildPageStructure()
      await this._renderCurrentPage()
      this._updatePageNav()
      this._setupStartSigning()
      this.pushEvent("pdf_loaded", { page_count: this.pageCount })
    } catch (err) {
      console.error("PDF load error:", err)
      this._wrapper.innerHTML = `
        <div class="flex items-center justify-center min-h-48 text-error/60">
          Failed to load PDF
        </div>
      `
      this.pushEvent("pdf_error", { message: err.message || "Failed to load PDF" })
    }
  },

  _buildPageStructure() {
    this._wrapper.innerHTML = `
      <div data-pdf-stage style="position:relative;display:inline-block;">
        <canvas data-pdf-canvas style="display:block;"></canvas>
        <div data-field-overlay style="position:absolute;top:0;left:0;width:100%;height:100%;"></div>
      </div>
    `
    this._stage = this._wrapper.querySelector("[data-pdf-stage]")
    this._canvas = this._wrapper.querySelector("[data-pdf-canvas]")
    this._overlay = this._wrapper.querySelector("[data-field-overlay]")
    this._ctx = this._canvas.getContext("2d")
  },

  async _renderCurrentPage() {
    if (!this.pdfDoc || !this._canvas) return

    if (this.rendering) {
      this.pendingPage = this.currentPage
      return
    }
    this.rendering = true

    try {
      const page = await this.pdfDoc.getPage(this.currentPage)
      this.scale = this._fitScale(page)
      const vp = page.getViewport({ scale: this.scale })

      const dpr = window.devicePixelRatio || 1
      this._canvas.width = vp.width * dpr
      this._canvas.height = vp.height * dpr
      this._canvas.style.width = vp.width + "px"
      this._canvas.style.height = vp.height + "px"

      this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      await page.render({ canvasContext: this._ctx, viewport: vp }).promise

      this._renderFieldOverlays()
    } catch (err) {
      console.error("PDF render error:", err)
    } finally {
      this.rendering = false
      if (this.pendingPage !== null) {
        this.currentPage = this.pendingPage
        this.pendingPage = null
        this._renderCurrentPage()
      }
    }
  },

  // Shrinks the page to the wrapper's width on narrow screens (a letter page
  // at 1.0 is 612px, wider than any phone), never enlarging past data-scale.
  _fitScale(page) {
    const maxScale = parseFloat(this.el.dataset.scale) || 1.0
    const style = getComputedStyle(this._wrapper)
    const available =
      this._wrapper.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
    const pageWidth = page.getViewport({ scale: 1.0 }).width
    return available > 0 ? Math.min(maxScale, available / pageWidth) : maxScale
  },

  // Resolves once the page is rendered so callers can act on its overlays.
  async _goToPage(page) {
    const p = parseInt(page, 10)
    if (p >= 1 && p <= this.pageCount && p !== this.currentPage) {
      this._saveOverlayValues()
      this.currentPage = p
      this._updatePageNav()
      await this._renderCurrentPage()
    }
  },

  // =========================================================================
  // Page navigation
  // =========================================================================

  _setupPageNav() {
    this._nav = this.el.querySelector("[data-page-nav]")
    if (!this._nav) return

    this._navLabel = this._nav.querySelector("[data-page-label]")
    this._navPrev = this._nav.querySelector("[data-page-prev]")
    this._navNext = this._nav.querySelector("[data-page-next]")

    this._navPrev.addEventListener("click", () => this._goToPage(this.currentPage - 1))
    this._navNext.addEventListener("click", () => this._goToPage(this.currentPage + 1))
  },

  // "Start signing" jump: the signer's fields may sit pages deep (a signature
  // block on the last page of a contract) or below the fold of a long first
  // page, with nothing pointing there. Jumps to the first field still
  // waiting on this signer.
  _setupStartSigning() {
    const submitBtn = this.el.querySelector("[data-submit-btn]")
    if (!submitBtn) return
    if (this._pendingFields().length === 0) return

    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.startSigning = ""
    btn.className = this.pageCount > 1 ? "btn btn-primary btn-xs" : "btn btn-ghost btn-sm"
    btn.textContent = this._signatureFields().length > 0 ? "Start signing →" : "Go to fields →"
    btn.addEventListener("click", () => this._jumpToNextField())

    if (this.pageCount > 1 && this._nav) {
      this._nav.appendChild(btn)
    } else {
      submitBtn.parentNode.insertBefore(btn, submitBtn)
    }
  },

  // This signer's fields that still need a value, in document order.
  _pendingFields() {
    return this.fields
      .filter(f => !f.readonly && !(f._value || ""))
      .sort((a, b) => (a.page || 1) - (b.page || 1) || a.y - b.y || a.x - b.x)
  },

  _signatureFields() {
    return this.fields.filter(f => !f.readonly && (f.type === "signature" || f.type === "initials"))
  },

  async _jumpToNextField() {
    this._saveOverlayValues()
    const [next] = this._pendingFields()
    if (!next) return
    await this._goToPage(next.page || 1)
    this._focusField(next)
  },

  _focusField(field) {
    if (!this._overlay) return
    const container = this._overlay.querySelector(`div[data-field-id="${field.id}"]`)
    if (!container) return
    // Instant rather than smooth: smooth scrolling is skipped entirely when the
    // tab isn't focused, which left signers staring at the top of the page.
    container.scrollIntoView({ block: "center" })
    const input = container.querySelector("input")
    if (input) input.focus({ preventScroll: true })
  },

  _updatePageNav() {
    if (!this._nav) return

    // Single-page documents don't need controls
    const show = this.pageCount > 1
    this._nav.classList.toggle("hidden", !show)
    this._nav.classList.toggle("flex", show)
    if (!show) return

    if (!this._navPageInput) {
      this._navLabel.innerHTML = ""
      this._navLabel.append("Page ")
      const input = document.createElement("input")
      input.type = "number"
      input.min = "1"
      input.className = "input input-xs input-bordered w-12 text-center mx-1"
      input.addEventListener("change", () => {
        this._goToPage(input.value)
        input.value = this.currentPage
      })
      this._navLabel.appendChild(input)
      this._navTotal = document.createTextNode("")
      this._navLabel.appendChild(this._navTotal)
      this._navPageInput = input
    }

    this._navPageInput.value = this.currentPage
    this._navPageInput.max = String(this.pageCount)
    this._navTotal.textContent = ` of ${this.pageCount}`
    this._navPrev.disabled = this.currentPage <= 1
    this._navNext.disabled = this.currentPage >= this.pageCount
  },

  // =========================================================================
  // Field Overlays (HTML inputs positioned over PDF)
  // =========================================================================

  _renderFieldOverlays() {
    if (!this._overlay) return
    this._overlay.innerHTML = ""

    const pageFields = this.fields.filter(f => f.page === this.currentPage)
    for (const field of pageFields) {
      this._overlay.appendChild(this._buildInput(field))
    }
  },

  _buildInput(field) {
    const sx = field.x * this.scale
    const sy = field.y * this.scale
    const sw = field.w * this.scale
    const sh = field.h * this.scale

    const container = document.createElement("div")
    container.dataset.fieldId = field.id
    container.style.cssText = `
      position:absolute;
      left:${sx}px; top:${sy}px;
      width:${sw}px; height:${sh}px;
    `

    if (field.readonly) {
      container.appendChild(this._buildReadonlyField(field, sw, sh))
    } else if (field.type === "signature" || field.type === "initials") {
      container.appendChild(this._buildSignatureField(field, sw, sh))
    } else if (field.type === "checkbox") {
      const input = document.createElement("input")
      input.type = "checkbox"
      input.className = "checkbox checkbox-primary"
      input.style.cssText = `width:100%;height:100%;cursor:pointer;`
      if (field._value === "true" || field._value === true) input.checked = true
      input.dataset.fieldId = field.id
      input.name = `field_${field.id}`
      this._trackInput(input, field)
      container.appendChild(input)
    } else if (field.type === "date") {
      if (this._dateAutofillPending(field)) {
        container.appendChild(this._buildDatePill(field, sw, sh))
      } else {
        container.appendChild(this._buildDateInput(field, sh))
      }
    } else {
      const input = document.createElement("input")
      input.type = "text"
      input.placeholder = field.label || ""
      input.className = "pdf-fill-input"
      input.style.setProperty("--pdf-field-font", this._fieldFontSize(sh))
      if (field._value) input.value = field._value
      input.dataset.fieldId = field.id
      input.name = `field_${field.id}`
      this._trackInput(input, field)
      container.appendChild(input)
    }

    return container
  },

  // Prompt text inside a signature box or date pill, shrunk to fit the box's
  // width (~0.55em per character) so it never clips on a scaled-down page.
  _buildBoxLabel(text, sw, sh) {
    const label = document.createElement("span")
    const size = Math.min(sh * 0.45, 14, (sw - 6) / (text.length * 0.55))
    label.style.cssText = `font-size:${size}px;color:oklch(0.5 0.15 265);pointer-events:none;white-space:nowrap;`
    label.textContent = text
    return label
  },

  // Text fills ~60% of the box so it never clips, capped for tall boxes.
  _fieldFontSize(sh) {
    return `${Math.min(16, sh * 0.6)}px`
  },

  _buildDateInput(field, sh) {
    const input = document.createElement("input")
    input.type = "date"
    input.className = "pdf-fill-input"
    input.style.setProperty("--pdf-field-font", this._fieldFontSize(sh))
    if (field._value) input.value = field._value
    input.dataset.fieldId = field.id
    input.name = `field_${field.id}`
    this._trackInput(input, field)
    return input
  },

  // An empty date next to an unsigned signature will be filled with today's
  // date the moment the signer signs, so it reads as a promise rather than
  // an empty picker. Clicking it opens the picker for anyone who needs a
  // different date.
  _dateAutofillPending(field) {
    return !(field._value || "") && !field._manualDate && this._signatureFields().length > 0
  },

  _buildDatePill(field, sw, sh) {
    const pill = document.createElement("div")
    pill.dataset.datePill = ""
    pill.title = "Click to enter a different date"
    pill.style.cssText = `
      width:100%;height:100%;
      border:2px dashed oklch(0.65 0.24 265 / 0.5);
      border-radius:4px;
      background:oklch(0.65 0.24 265 / 0.05);
      cursor:pointer;
      display:flex;align-items:center;justify-content:center;
      overflow:hidden;
      box-sizing:border-box;
    `
    pill.appendChild(this._buildBoxLabel("Filled when you sign", sw, sh))

    pill.addEventListener("click", (e) => {
      e.preventDefault()
      field._manualDate = true
      const input = this._buildDateInput(field, sh)
      pill.replaceWith(input)
      input.focus()
      if (typeof input.showPicker === "function") {
        try { input.showPicker() } catch (_) {}
      }
    })

    return pill
  },

  // Persist the value onto the field definition as the user types, so it
  // survives page navigation and is never lost if the DOM is gone at submit.
  _trackInput(input, field) {
    const save = () => {
      field._value = input.type === "checkbox" ? input.checked.toString() : input.value
    }
    input.addEventListener("input", save)
    input.addEventListener("change", save)
  },

  // Another signer's field: shown greyed out, never editable. Displays any
  // value they already submitted, or a placeholder.
  _buildReadonlyField(field, sw, sh) {
    const wrapper = document.createElement("div")
    wrapper.dataset.fieldId = field.id
    wrapper.style.cssText = `
      width:100%;height:100%;
      border:1px dashed oklch(0.7 0 0 / 0.6);
      border-radius:4px;
      background:oklch(0.7 0 0 / 0.08);
      display:flex;align-items:center;
      padding:0 6px;
      overflow:hidden;
      box-sizing:border-box;
      cursor:not-allowed;
    `

    const value = field._value || ""
    const label = document.createElement("span")
    label.style.cssText = `font-size:${Math.max(9, sh * 0.4)}px;color:oklch(0.45 0 0);pointer-events:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`

    if (field.type === "signature" || field.type === "initials") {
      label.textContent = value === "[signed]" ? "Signed" : (field.label || "Another signer")
    } else {
      label.textContent = value && value !== "false" ? value : (field.label || "Another signer")
    }

    wrapper.appendChild(label)
    return wrapper
  },

  // =========================================================================
  // Signature / Initials Field
  // =========================================================================

  _buildSignatureField(field, sw, sh) {
    const wrapper = document.createElement("div")
    wrapper.dataset.fieldId = field.id
    wrapper.style.cssText = `
      width:100%;height:100%;
      border:2px dashed oklch(0.65 0.24 265 / 0.5);
      border-radius:4px;
      background:oklch(0.65 0.24 265 / 0.05);
      cursor:pointer;
      display:flex;align-items:center;justify-content:center;
      overflow:hidden;
      box-sizing:border-box;
    `

    if (field._value && field._value.startsWith("data:")) {
      // Show signature preview
      const img = document.createElement("img")
      img.src = field._value
      img.style.cssText = `max-width:100%;max-height:100%;object-fit:contain;pointer-events:none;`
      wrapper.appendChild(img)
    } else {
      const text = field.type === "initials" ? "Click to initial" : "Click to sign"
      wrapper.appendChild(this._buildBoxLabel(text, sw, sh))
    }

    wrapper.addEventListener("click", (e) => {
      e.preventDefault()
      this._openSignatureModal(field)
    })

    return wrapper
  },

  _openSignatureModal(field) {
    openSignatureModal({
      isInitials: field.type === "initials",
      initialDataUrl: field._value || "",
      signerName: this.signerName,
      consentText: this.consentText,
      onAccept: (dataUrl) => {
        field._value = dataUrl
        if (dataUrl) this._autofillDates()
        this._renderFieldOverlays()
      },
    })
  },

  // After signing, prefill this signer's empty date fields with today's
  // date. Still visible and editable before submit; fields on the current
  // page update in place, other pages pick the value up when rendered.
  _autofillDates() {
    const today = new Date()
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
    for (const field of this.fields) {
      if (!field.readonly && field.type === "date" && !(field._value || "")) {
        field._value = iso
        const input = this._overlay && this._overlay.querySelector(`input[data-field-id="${field.id}"]`)
        if (input) input.value = iso
      }
    }
  },

  // =========================================================================
  // Value Collection
  // =========================================================================

  _saveOverlayValues() {
    if (!this._overlay) return
    // Save values from regular inputs
    this._overlay.querySelectorAll("input[data-field-id]").forEach(input => {
      const id = parseInt(input.dataset.fieldId, 10)
      const field = this.fields.find(f => f.id === id)
      if (!field) return
      if (field.type === "signature" || field.type === "initials") return // handled via modal
      field._value = input.type === "checkbox" ? input.checked.toString() : input.value
    })
  },

  _collectFieldValues() {
    this._saveOverlayValues()
    const values = {}
    for (const field of this.fields) {
      if (field.readonly) continue
      // Keyed by id, not label: labels are editable and need not be unique.
      // Must match Pdf.custom_field_key/1.
      const key = `custom_field_${field.id}`
      if (field.type === "signature" || field.type === "initials") {
        values[key] = field._value ? "[signed]" : ""
      } else {
        values[key] = field._value || ""
      }
    }
    return values
  },

  _collectSignatures() {
    const sigs = []
    for (const field of this.fields) {
      if (field.readonly) continue
      if ((field.type === "signature" || field.type === "initials") && field._value) {
        sigs.push({
          field_id: field.id,
          // Human name for the audit trail and certificate
          field_name: field.label || `field_${field.id}`,
          data_url: field._value,
        })
      }
    }
    return sigs
  },

  // =========================================================================
  // Submit: Generate filled PDF with pdf-lib and upload
  // =========================================================================

  async _handleSubmit() {
    this._saveOverlayValues()

    // Validate required fields (only this signer's — readonly fields
    // belong to other signers)
    const missingFields = this.fields
      .filter(f => f.required && !f.readonly)
      .filter(f => {
        const value = f._value || ""
        if (f.type === "signature" || f.type === "initials") {
          return !value || !value.startsWith("data:")
        }
        if (f.type === "checkbox") {
          return value !== "true"
        }
        return !value
      })
      .map(f => f.label || f.type)

    if (missingFields.length > 0) {
      this.pushEvent("validation_error", { message: `Please fill in required fields: ${missingFields.join(", ")}` })
      return
    }

    // Optional fields left empty are easy to miss (e.g. a date on another
    // page) — ask before submitting without them.
    const emptyFields = this.fields
      .filter(f => !f.readonly && !(f._value || ""))
      .map(f => f.label || f.type)

    if (emptyFields.length > 0) {
      const ok = window.confirm(
        `Some fields are still empty: ${emptyFields.join(", ")}.\n\nSubmit anyway?`
      )
      if (!ok) return
    }

    const submitBtn = this.el.querySelector("[data-submit-btn]")
    if (submitBtn) {
      this._submitLabel = submitBtn.innerHTML
      submitBtn.disabled = true
      submitBtn.innerHTML = `<span class="loading loading-spinner loading-sm"></span> Processing...`
    }

    // Multi-signer: only this signer's values + signatures are sent; the
    // final PDF is composed server-side once everyone has signed.
    if (this.multiSigner) {
      const signatures = this._collectSignatures()
      this.pushEvent("custom_submit", {
        field_values: this._collectFieldValues(),
        signatures,
        // A signature only exists once Accept was clicked under the consent line.
        esign_consent: signatures.length > 0,
      })
      return
    }

    try {
      const { PDFDocument, rgb, StandardFonts } = await loadPdfLib()
      const pdfDoc = await PDFDocument.load(this._originalPdfBytes.slice(0))
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica)

      await drawFields(
        pdfDoc,
        this.fields.map(field => ({ ...field, value: field._value })),
        {
          font,
          rgb,
          toBytes: base64 => Uint8Array.from(atob(base64), c => c.charCodeAt(0)),
        }
      )

      const filledBytes = await pdfDoc.save()
      const blob = new Blob([filledBytes], { type: "application/pdf" })
      const file = new File([blob], "filled.pdf", { type: "application/pdf" })

      // Upload via LiveView
      this.upload("filled_pdf", [file])

      // Send field values and signature audit data
      const fieldValues = this._collectFieldValues()
      const signatures = this._collectSignatures()
      this.pushEvent("custom_submit", {
        field_values: fieldValues,
        signatures,
        esign_consent: signatures.length > 0,
      })
    } catch (err) {
      console.error("PDF fill error:", err)
      this.pushEvent("pdf_error", { message: "Failed to generate filled PDF: " + err.message })
      if (submitBtn) {
        submitBtn.disabled = false
        submitBtn.innerHTML = this._submitLabel
      }
    }
  },
}

export default PdfFormFiller
